/**
 * Thin fetch wrapper used by every call to ops-api.
 *
 * Responsibilities:
 *   - Attach `credentials: 'include'` so the session cookie round-trips on
 *     cross-origin calls (`ops.*` → `ops.api.*`).
 *   - Default JSON content-type on bodied requests.
 *   - Normalize non-2xx responses into `OpsApiError` carrying the ADR-004
 *     error envelope so callers don't each re-parse it.
 *   - Forward network-level failures as a `SERVICE_UNAVAILABLE`-shaped
 *     error so UI code can branch on `.code` uniformly.
 *
 * Base URL comes from `import.meta.env.VITE_OPS_API_BASE_URL` at build
 * time. Local dev without an env points at `http://localhost:8787` which
 * matches `wrangler dev` defaults for the ops-api worker.
 */

const DEFAULT_BASE_URL = 'http://localhost:8787';

interface ErrorEnvelope {
  error: {
    code: string;
    message: string;
    retryable: boolean;
  };
}

export class OpsApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly retryable: boolean;
  constructor(status: number, code: string, message: string, retryable: boolean) {
    super(message);
    this.name = 'OpsApiError';
    this.status = status;
    this.code = code;
    this.retryable = retryable;
  }
}

export interface OpsApiRequestInit {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

export function getOpsApiBaseUrl(): string {
  const fromEnv = import.meta.env.VITE_OPS_API_BASE_URL;
  if (typeof fromEnv === 'string' && fromEnv.length > 0) {
    return fromEnv.replace(/\/+$/, '');
  }
  return DEFAULT_BASE_URL;
}

/**
 * Issue a request against ops-api. Returns parsed JSON body (typed by
 * caller). Throws `OpsApiError` on any non-2xx.
 *
 * Kept deliberately tiny — no caching, no retries, no interceptor stack.
 * Wave 5 can layer higher-level client code on top without reshaping this.
 */
export async function opsApiRequest<T>(
  path: string,
  init: OpsApiRequestInit = {},
  fetchImpl: typeof fetch = fetch,
): Promise<T> {
  const baseUrl = getOpsApiBaseUrl();
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const url = `${baseUrl}${normalizedPath}`;

  const headers: Record<string, string> = { ...(init.headers ?? {}) };
  let body: BodyInit | undefined;
  if (init.body !== undefined) {
    body = JSON.stringify(init.body);
    if (headers['Content-Type'] === undefined && headers['content-type'] === undefined) {
      headers['Content-Type'] = 'application/json';
    }
  }

  let response: Response;
  try {
    response = await fetchImpl(url, {
      method: init.method ?? (body === undefined ? 'GET' : 'POST'),
      credentials: 'include',
      headers,
      body,
      signal: init.signal,
    });
  } catch (cause) {
    const message = cause instanceof Error ? cause.message : 'network request failed';
    throw new OpsApiError(0, 'SERVICE_UNAVAILABLE', message, true);
  }

  const rawText = await response.text();
  const parsed = rawText.length === 0 ? null : safeJsonParse(rawText);

  if (!response.ok) {
    const envelope = asErrorEnvelope(parsed);
    if (envelope !== null) {
      throw new OpsApiError(
        response.status,
        envelope.error.code,
        envelope.error.message,
        envelope.error.retryable,
      );
    }
    throw new OpsApiError(
      response.status,
      'SERVICE_UNAVAILABLE',
      `ops-api returned ${response.status} with non-envelope body`,
      response.status >= 500,
    );
  }

  return parsed as T;
}

function safeJsonParse(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function asErrorEnvelope(raw: unknown): ErrorEnvelope | null {
  if (raw === null || typeof raw !== 'object') {
    return null;
  }
  const error = (raw as { error?: unknown }).error;
  if (error === null || typeof error !== 'object') {
    return null;
  }
  const code = (error as { code?: unknown }).code;
  const message = (error as { message?: unknown }).message;
  const retryable = (error as { retryable?: unknown }).retryable;
  if (typeof code !== 'string' || typeof message !== 'string' || typeof retryable !== 'boolean') {
    return null;
  }
  return { error: { code, message, retryable } };
}
